import dayjs from 'dayjs';
import { Suspense, type ReactElement } from 'react';
import {
  StageExecutionsTable,
  type StageExecutionRow,
} from '@/features/pipelines/components/StageExecutionsTable';
import { queryPipelineStageExecutions } from '@/features/pipelines/data';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { DeferredTablePanelSkeleton } from '@/features/shared/components/deferred/DeferredSectionSkeletons';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getDetailLabel } from '@/shared/copy/details';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Box, type GridColumnVisibilityModel } from '@/ui/primitives';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, QueryResult } from '@flowqa/shared';

export type StageExecutionSortKey =
  | 'pipelineName'
  | 'stageName'
  | 'status'
  | 'startedAt'
  | 'finishedAt'
  | 'durationMs';

type StageExecutionsToolbarProps = {
  searchAriaLabel?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  sortKey: StageExecutionSortKey;
  sortDirection: 'asc' | 'desc';
  onSortChange: (nextSortKey: StageExecutionSortKey, nextSortDirection: 'asc' | 'desc') => void;
  filterStatuses: BuildStatus[];
  onFilterStatusesChange: (next: BuildStatus[]) => void;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  fromDate: dayjs.Dayjs | null;
  toDate: dayjs.Dayjs | null;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
  onFromDateChange: (next: dayjs.Dayjs | null) => void;
  onToDateChange: (next: dayjs.Dayjs | null) => void;
  availableTags: string[];
  summaryCount: number | null;
  summarySingularLabel?: string;
  summaryPluralLabel?: string;
};

export const StageExecutionsToolbar = ({
  searchAriaLabel = 'Search stage executions',
  searchValue,
  onSearchChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  onSortChange,
  filterStatuses,
  onFilterStatusesChange,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay,
  latestQueryEndDay,
  onFromDateChange,
  onToDateChange,
  availableTags,
  summaryCount,
  summarySingularLabel = 'stage execution',
  summaryPluralLabel = 'stage executions',
}: StageExecutionsToolbarProps): ReactElement => (
  <Box sx={{ flex: '0 0 auto' }}>
    <TableToolbar<StageExecutionSortKey, BuildStatus>
      title={undefined}
      searchAriaLabel={searchAriaLabel}
      searchValue={searchValue}
      onDebouncedSearchChange={onSearchChange}
      columnVisibilityModel={columnVisibilityModel}
      defaultColumnVisibilityModel={columnVisibilityModel}
      columnOptions={[
        { field: 'stageName', label: getDetailLabel('stage') },
        { field: 'buildKey', label: getDetailLabel('run') },
        { field: 'status', label: getDetailLabel('status') },
        { field: 'jobStatusCounts', label: 'Jobs' },
      ]}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      sortKey={sortKey}
      sortDirection={sortDirection}
      defaultSortKey="startedAt"
      defaultSortDirection="desc"
      sortOptions={[
        { key: 'pipelineName', label: getDetailLabel('pipeline') },
        { key: 'stageName', label: getDetailLabel('stage') },
        { key: 'status', label: getDetailLabel('status') },
        { key: 'startedAt', label: getDetailLabel('started') },
        { key: 'finishedAt', label: getDetailLabel('finished') },
        { key: 'durationMs', label: getDetailLabel('duration') },
      ]}
      onSortChange={onSortChange}
      filterStatuses={filterStatuses}
      statusOptions={buildStatusOptions}
      onFilterStatusesChange={onFilterStatusesChange}
      filterTags={filterTags}
      onFilterTagsChange={onFilterTagsChange}
      dateQuickFilter={dateQuickFilter}
      onDateQuickFilterChange={onDateQuickFilterChange}
      fromDate={fromDate}
      toDate={toDate}
      latestQueryStartDay={latestQueryStartDay}
      latestQueryEndDay={latestQueryEndDay}
      onFromDateChange={onFromDateChange}
      onToDateChange={onToDateChange}
      availableTags={availableTags}
      summaryCount={summaryCount}
      summarySingularLabel={summarySingularLabel}
      summaryPluralLabel={summaryPluralLabel}
      hideDateRangeLabel
      showControlsWithoutSummary
    />
  </Box>
);

type StageExecutionsTabContentProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  dateQuickFilter: DateQuickFilter;
  searchValue: string;
  sortKey: StageExecutionSortKey;
  sortDirection: 'asc' | 'desc';
  filterStatuses: BuildStatus[];
  filterPipelineIds: string[];
  filterStageNames: string[];
  filterTags: string[];
  startDay: string | null;
  endDay: string | null;
  hasCustomBounds: boolean;
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
};

export const StageExecutionsTabContent = ({
  selectedProjectId,
  selectedOrganisationId,
  dateQuickFilter,
  searchValue,
  sortKey,
  sortDirection,
  filterStatuses,
  filterPipelineIds,
  filterStageNames,
  filterTags,
  startDay,
  endDay,
  hasCustomBounds,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: StageExecutionsTabContentProps): ReactElement => (
  <Suspense
    fallback={<DeferredTablePanelSkeleton embedded titleWidth={160} variant="stageExecutions" />}
  >
    <StageExecutionsTable
      rows={[]}
      queryKey={queryKeys.dataGrid(
        'pipelines/stage-executions-table',
        selectedOrganisationId ?? null,
        selectedProjectId ?? null,
        dateQuickFilter,
        searchValue,
        sortKey,
        sortDirection,
        [...filterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
        [...filterPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
        [...filterStageNames].sort((left, right) => left.localeCompare(right)).join(','),
        [...filterTags].sort((left, right) => left.localeCompare(right)).join(','),
        startDay,
        endDay,
      )}
      queryFn={async (input): Promise<QueryResult<StageExecutionRow>> =>
        await queryPipelineStageExecutions(selectedProjectId, selectedOrganisationId ?? undefined, {
          ...input,
          search: searchValue,
          sorts:
            input.sorts.length > 0
              ? input.sorts
              : ([{ field: sortKey, direction: sortDirection }] satisfies ApiSort[]),
          filters: [
            ...input.filters,
            ...mergeFilters(
              buildInFilter('status', filterStatuses),
              buildInFilter('pipelineId', filterPipelineIds),
              buildInFilter('stageName', filterStageNames),
              buildInFilter('tags', filterTags),
              buildDateRangeFilters(
                'startedAt',
                startDay ? dayjs(startDay).startOf('day').toISOString() : null,
                endDay ? dayjs(endDay).endOf('day').toISOString() : null,
              ),
            ),
          ],
        })
      }
      pageSizeOptions={[5, 10, 25, 50]}
      initialPageSize={5}
      noRowsText={
        hasCustomBounds
          ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'stageExecution' })
          : formatRangeWindowNoDataText(
              getEmptyStateMessage('entity.noneFound', { entity: 'stageExecution' }),
              dateQuickFilter,
              false,
            )
      }
      search={searchValue}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
    />
  </Suspense>
);
